// src/App.js
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/Layout';
import LoadingPage from './pages/LoadingPage';
import { ErrorBoundary } from './ErrorBoundary'; // Fixed import to use named export
import { initClientOptimizations } from './utils/criticalCss';
import GoogleAnalytics from './components/GoogleAnalytics'; // Import Google Analytics tracker
import ScrollToTop from './components/ScrollToTop'; // Import scroll to top component
// Initialize dynamic URL mapping for automatic category support
import { initializeUrlMapping } from './utils/dynamicUrlMapping';

import './App.css';
import './Theme.css';
import './styles/fontOptimization.css'; // Import font optimization styles

// Import legal pages directly to avoid suspense during prerendering
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccessibilityStatement from './pages/AccessibilityStatement';
import Disclaimer from './components/Disclaimer';

// Lazy load other page components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const ProductList = lazy(() => import('./pages/ProductList'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const FavoritedProducts = lazy(() => import('./pages/FavoritedProducts'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const DefaultRedirect = lazy(() => import('./components/DefaultRedirect'));
const UserLogsViewer = lazy(() => import('./components/UserLogsViewer'));

function App() {
  const location = useLocation();
  const [metaDescription, setMetaDescription] = useState("OriginSelect connects you with authentic, handcrafted products from local artisans. Discover unique, high-quality goods made with heart and heritage.");
  const [hydrateComplete, setHydrateComplete] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Initialize prerendering state safely to prevent hydration mismatches
  const [isPrerendering, setIsPrerendering] = useState(() => {
    // Only detect prerendering on client-side to avoid SSR/client differences
    return false;
  });

  // Disable browser's automatic scroll restoration on app initialization
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Initialize dynamic URL mapping system for automatic category support
  useEffect(() => {
    const initUrlMapping = async () => {
      try {
        await initializeUrlMapping();
        console.log('URL mapping initialized successfully');
      } catch (error) {
        console.warn('Failed to initialize URL mapping:', error);
      }
    };

    initUrlMapping();
  }, []);
  
  useEffect(() => {
    // Check if this is a prerendering environment - only on client side
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap') {
      setIsPrerendering(true);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-prerendered', 'true');
      }
    }
    
    let description = "OriginSelect connects you with authentic, handcrafted products from local artisans. Discover unique, high-quality goods made with heart and heritage.";
    // Set page-specific meta descriptions
    if (location.pathname.includes('/about')) {
      description = "Learn about OriginSelect's mission to connect consumers with authentic, locally crafted products that celebrate cultural heritage and craftsmanship.";
    } else if (location.pathname.includes('/blog')) {
      description = "Read the latest insights, tips, and stories about sustainable, quality products from OriginSelect. Discover how we champion products made with heart and heritage.";
    } else if (location.pathname.includes('/products') && !location.pathname.includes('/products/')) {
      description = "Discover a curated selection of authentic, handcrafted products from local artisans. Shop our collection of high-quality, locally sourced goods.";
    } else if (location.pathname.includes('/contact')) {
      description = "Get in touch with OriginSelect. We're here to help you find authentic, locally crafted products and answer your questions.";
    } else if (location.pathname.includes('/search')) {
      description = "Search our collection of authentic, handcrafted products from local artisans. Find the perfect unique, high-quality items.";
    } else if (location.pathname.includes('/privacy')) {
      description = "OriginSelect Privacy Policy - Learn how we collect, use, and protect your personal information according to PIPEDA requirements.";
    } else if (location.pathname.includes('/accessibility')) {
      description = "OriginSelect Accessibility Statement - Our commitment to making our website accessible to all users.";
    } else if (location.pathname.includes('/disclaimer')) {
      description = "OriginSelect Disclaimer and Terms of Use - Legal information about using our website and services.";
    }
    setMetaDescription(description);
  }, [location]);

  // Track route changes
  useEffect(() => {
    // Only scroll to top on navigation changes, not initial load
    if (!isInitialLoad && typeof window !== 'undefined') {
      // Use multiple methods to ensure scroll to top works for all navigation types
      // including browser back/forward buttons
      const scrollToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };

      // Immediate scroll
      requestAnimationFrame(scrollToTop);
      
      // Also force scroll after a short delay to override any browser restoration
      setTimeout(scrollToTop, 10);
    } else {
      setIsInitialLoad(false);
    }

    // Reset the hydration state when route changes
    setHydrateComplete(false);
    
    // Initialize client-side optimizations for the current route
    initClientOptimizations();
    
    // Mark hydration as complete after a short delay
    // This helps ensure smooth transitions between routes
    const timer = setTimeout(() => {
      setHydrateComplete(true);
      
      // Ensure scroll position is at top after content loads
      // This is especially important for back button navigation
      if (!isInitialLoad && typeof window !== 'undefined') {
        const forceScrollToTop = () => {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        };
        forceScrollToTop();
      }
      
      // Dispatch render-event for prerendering
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event('render-event'));
        
        // Add a marker element to indicate content is fully loaded (for PrerenderSPA detection)
        if (!document.getElementById('content-fully-loaded')) {
          const marker = document.createElement('div');
          marker.id = 'content-fully-loaded';
          marker.style.display = 'none';
          document.body.appendChild(marker);
        }
        
        // Set prerender-specific attribute for hydration
        if (document.getElementById('root')) {
          document.getElementById('root').setAttribute('data-prerendered', 'true');
        }
        
        // Special handling for legal pages
        if (
          location.pathname.includes('/privacy') || 
          location.pathname.includes('/disclaimer') || 
          location.pathname.includes('/accessibility')
        ) {
          document.documentElement.setAttribute('data-legal-page', 'true');
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname, isInitialLoad]);

  // Add this function to optimize resource loading
  useEffect(() => {
    // Only run on client and first load
    if (typeof window !== 'undefined' && isInitialLoad) {
      // Preload critical fonts to prevent layout shifts
      const fontPreloads = [
        { href: '/fonts/inter-var.woff2', type: 'font/woff2', crossOrigin: 'anonymous' },
        // Add any other critical fonts your site uses
      ];
      
      fontPreloads.forEach(font => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = font.href;
        link.as = 'font';
        link.type = font.type;
        link.crossOrigin = font.crossOrigin;
        document.head.appendChild(link);
      });
      
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  // Enhanced loading component with proper hydration handling
  const LoadingComponent = () => (
    <div className={`app-loading-container ${hydrateComplete ? 'fade-out' : ''}`}>
      <LoadingPage />
    </div>
  );

  // For prerendering environments, render a simpler version without suspense
  if (isPrerendering && (
      location.pathname.includes('/privacy') || 
      location.pathname.includes('/disclaimer') || 
      location.pathname.includes('/accessibility')
    )) {
    let LegalComponent;
    if (location.pathname.includes('/privacy')) {
      LegalComponent = <PrivacyPolicy />;
    } else if (location.pathname.includes('/disclaimer')) {
      LegalComponent = <Disclaimer />;
    } else if (location.pathname.includes('/accessibility')) {
      LegalComponent = <AccessibilityStatement />;
    }
    
    return (
      <div className="app legal-prerendered">
        {LegalComponent}
      </div>
    );
  }

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <div className="app">
          {/* Google Analytics tracking component */}
          <GoogleAnalytics />
          {/* Scroll to top on route changes */}
          <ScrollToTop />
          <Suspense fallback={<LoadingComponent />}>
            <Routes>
              {/* Default redirect handling */}
              <Route path="/" element={<DefaultRedirect />} />

              {/* Standalone routes for privacy and disclaimer, redirecting to a default country version */}
              <Route path="/privacy" element={<Navigate to="/country/canada/privacy" replace />} />
              <Route path="/disclaimer" element={<Navigate to="/country/canada/disclaimer" replace />} />
              <Route path="/accessibility" element={<Navigate to="/country/canada/accessibility" replace />} />
              
              {/* Country-specific routes */}
              <Route path="/country/:countryCode" element={<Layout />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/:postSlug" element={<BlogPost />} />
                <Route path="contact" element={<Contact />} />
                <Route path="unsubscribe" element={<Unsubscribe />} />
                <Route path="products" element={<ProductList />} />
                <Route path="products/:productId" element={<ProductDetail />} />
                <Route path="products/:productId/name/:productName" element={<ProductDetail />} />
                
                {/* Clean URL routes for categories */}
                <Route path=":category" element={<ProductList />} />
                <Route path=":category/:subcategory" element={<ProductList />} />
                
                <Route path="search" element={<SearchPage />} />
                <Route path="login" element={<Login />} />
                <Route path="favorites" element={<FavoritedProducts />} />
                {/* Development route for viewing user logs */}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="user-logs" element={<UserLogsViewer />} />
                )}
                {/* Legal pages are not lazy loaded for better prerendering */}
                <Route path="privacy" element={<PrivacyPolicy />} />
                <Route path="disclaimer" element={<Disclaimer />} />
                <Route path="accessibility" element={<AccessibilityStatement />} />
              </Route>
              
              {/* Catch-all redirect */}
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
          </Suspense>
        </div>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
